/**
 * English — the one language of the site and the source of truth for UI strings. Keys are flat, dotted
 * and typed: `MessageId` is the union of these keys.
 *
 * Page copy (the home sections, About, Contact …) lives next to its page, not here: this table is
 * part of the first load, page copy is not. Validation messages coming from `@sbg/shared` are shown
 * as they are.
 */
export const en = {
  // ── Navigation ─────────────────────────────────────────────────────────────────────────────
  'nav.home': 'Home',
  'nav.shop': 'Shop',
  'nav.collection': 'Our Collections',
  'nav.applications': 'Where to Use',
  'nav.visualizer': 'Visualizer',
  'nav.gallery': 'Gallery',
  'nav.contact': 'Contact us',
  'nav.account': 'Account',
  'theme.toDark': 'Switch to the dark theme',
  'theme.toLight': 'Switch to the light theme',
  'nav.primary': 'Main',
  'nav.mobile': 'Mobile',
  'nav.footer': 'Footer',
  'nav.openMenu': 'Open menu',
  'nav.closeMenu': 'Close menu',
  'nav.menu': 'Menu',
  'nav.menuDescription': 'The pages of this website and your account.',
  'nav.skipToContent': 'Skip to main content',
  'nav.backToTop': 'Back to top',
  'nav.homeLink': '{brand} — home',
  'nav.breadcrumb': 'You are here',

  // ── Home: hero ─────────────────────────────────────────────────────────────────────────────
  'hero.eyebrow': 'Shree Balaji Granites · India',
  'hero.title': 'Strong stone. Lasting beauty.',
  'hero.lead':
    'Granite slabs, tiles and countertops from our Rajsamand factory. Pick your stone, see it in your room, ask for your price.',
  'hero.cta.shop': 'Browse all granite',
  'hero.cta.visualizer': 'Try a stone in your room',

  // ── Footer ─────────────────────────────────────────────────────────────────────────────────
  'footer.tagline':
    'Granite slabs, tiles and countertops from our factory in Rajsamand, Rajasthan, India',
  'footer.explore': 'Pages',
  'footer.getInTouch': 'Talk to us',
  'footer.visit': 'Visit us',
  'footer.follow': 'Follow us',
  'footer.legal': 'Legal',
  'footer.address': 'Address',
  'footer.hours': 'Open hours',
  'footer.phone': 'Phone',
  'footer.email': 'Email',
  'footer.whatsapp': 'WhatsApp',
  'footer.chatOnWhatsApp': 'Chat on WhatsApp',
  'footer.whatsappGreeting':
    'Hello Shree Balaji Granites! I am looking for granite for my home or project. Please call me with your advice and the best price.',
  'footer.legalDisclaimer': 'Legal Disclaimer',
  'footer.sitemap': 'Sitemap',
  'footer.openMap': 'Open in Google Maps',
  'disclaimer.changeChoice': 'Change my cookie choice',
  'footer.rights': '© {year} Shree Balaji Granites. All rights reserved.',

  // ── Cookie / analytics consent ─────────────────────────────────────────────────────────────
  'consent.region': 'Legal Disclaimer and cookies',
  'consent.title': 'Cookies and Legal Disclaimer',
  'consent.body':
    'We use cookies to make this site work and, if you allow, to count visits. By pressing a button you accept our {link}.',
  'consent.link': 'Legal Disclaimer',
  'consent.note':
    'By pressing a button you accept this Legal Disclaimer. “I accept” also allows cookies that count visits. The other button keeps them off.',
  'consent.accept': 'I accept',
  'consent.acceptEssential': 'Essential cookies only',

  // ── Common buttons & labels ────────────────────────────────────────────────────────────────
  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.save': 'Save',
  'common.submit': 'Submit',
  'common.send': 'Send',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.view': 'View',
  'common.details': 'Details',
  'common.enquiry': 'Enquiry',
  'common.whatsapp': 'WhatsApp',
  'common.backHome': 'Back to home',
  'common.learnMore': 'Read more',
  'common.opensInNewTab': '(opens in a new tab)',
  'common.optional': 'optional',
  'common.required': 'required',

  // ── Dialogs & toasts ───────────────────────────────────────────────────────────────────────
  'toast.region': 'Notifications',
  'confirm.reasonRequired': 'Please give a reason.',
  'unsaved.title': 'Leave without saving?',
  'unsaved.body': 'Your changes are not saved. If you leave now, you will lose them.',
  'unsaved.stay': 'Stay here',
  'unsaved.leave': 'Leave page',

  // ── Quick-contact dock ─────────────────────────────────────────────────────────────────────
  'dock.label': 'Quick contact',
  'dock.quote': 'Ask for a price',
  'dock.call': 'Call',

  // ── Calls to action ────────────────────────────────────────────────────────────────────────
  'cta.enquiry': 'Send us a message',

  // ── Product card, price, wishlist ──────────────────────────────────────────────────────────
  'product.detailsAria': 'Details of {name}',
  'product.enquiryAria': 'Enquiry about {name}',
  'product.wishlistLabel': 'Save {name} to your list',
  'product.save': 'Save',
  'product.saved': 'Saved',
  'product.featured': 'Featured',
  'product.new': 'New',
  'product.thickness': '{mm} mm',
  'product.noImage': 'Photo coming soon',
  'product.viewMacro': 'Close-up of the stone',
  'availability.IN_STOCK': 'In stock',
  'availability.LOW_STOCK': 'Only a few left',
  'availability.MADE_TO_ORDER': 'Made when you order',
  'availability.OUT_OF_STOCK': 'Out of stock',
  'price.onRequest': 'Ask for the price',
  'price.was': 'Old price',
  'price.now': 'New price',
  'price.off': '{percent}% off',
  'price.offSr': '{percent} percent off',
  'shop.noResults': 'No stones found.',
  'shop.loadError': 'We could not show the stones.',

  // ── Auth guards ────────────────────────────────────────────────────────────────────────────
  'auth.checking': 'Checking if you are signed in…',

  // ── Errors ─────────────────────────────────────────────────────────────────────────────────
  'errors.generic': 'Something went wrong. Please try again.',
  'errors.network': 'We could not connect. Check your internet and try again.',
  'errors.server': 'We have a problem on our side. Please try again in a little while.',
  'errors.rateLimited': 'Too many tries. Please wait a little and try again.',
  'errors.unauthenticated': 'Please sign in to continue.',
  'errors.sessionExpired': 'You were signed out. Please sign in again.',
  'errors.boundary.title': 'Something went wrong',
  'errors.boundary.body': 'We could not show this page. Try again, or go back to the home page.',
  'errors.chunk': 'The site has a new version, or your internet stopped. Please reload the page.',
  'errors.reload': 'Reload the page',
  'errors.notFound.title': 'Page not found',
  'errors.notFound.body': 'We cannot find this page. It may have moved.',
  'errors.forbidden.title': 'You cannot open this page',
  'errors.forbidden.body':
    'Your account cannot open this page. If this looks wrong, please contact us.',
} as const;

export type MessageId = keyof typeof en;
export type Messages = Readonly<Record<MessageId, string>>;
